import React, { useEffect, useState } from "react";
import ModalAddSecretaire from "./components/ModalAddSecretaire";
import CardSecretaires from "./components/CardSecretaires";
import { MdCheckCircle, MdVisibility, MdVisibilityOff } from "react-icons/md";
import secretairesService from "../../../service/secretaires.service";
import Swal from "sweetalert2";

const Secretaires = () => {
  const [secretaires, setSecretaires] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentSecretaire, setCurrentSecretaire] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [nameWarning, setNameWarning] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showCPassword, setShowCPassword] = useState(false);
  const [showPasswordEdit, setShowPasswordEdit] = useState(false);
  const [showCPasswordEdit, setShowCPasswordEdit] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  // Fetch all secretaires on mount
  const fetchSecretaires = async () => {
    setIsLoading(true);
    try {
      const response = await secretairesService.getAll();
      if (response && response.success && response.data) {
        setSecretaires(response.data);
      } else {
        throw new Error("Format de réponse inattendu");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de charger les secrétaires. Veuillez réessayer plus tard.",
        confirmButtonColor: "#57A863",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSecretaires();
  }, []);

  // Vérification des doublons de nom/prénom
  const checkDuplicateName = (firstName, lastName, currentId = null) => {
    return secretaires.some(
      (secretaire) =>
        secretaire.first_name?.toLowerCase() === firstName?.toLowerCase() &&
        secretaire.last_name?.toLowerCase() === lastName?.toLowerCase() &&
        secretaire.id !== currentId
    );
  };

  // Vérification des doublons d'email
  const checkDuplicateEmail = (email, currentId = null) => {
    return secretaires.some(
      (secretaire) =>
        secretaire.email_user?.toLowerCase() === email?.toLowerCase() &&
        secretaire.id !== currentId
    );
  };

  // Gestion des changements de nom
  const handleNameChange = (firstName, lastName) => {
    if (
      firstName &&
      lastName &&
      checkDuplicateName(firstName, lastName, currentSecretaire?.id)
    ) {
      setNameWarning(
        `Un utilisateur avec le nom "${firstName} ${lastName}" existe déjà.`
      );
    } else {
      setNameWarning("");
    }
  };

  const handleNew = () => {
    setCurrentSecretaire(null);
    setIsEditMode(false);
    setIsOpen(true);
    setNameWarning("");
    setFieldErrors({});
    setShowPassword(false);
    setShowCPassword(false);
    setShowPasswordEdit(false);
    setShowCPasswordEdit(false);
    setChangePassword(false);
  };

  const handleEdit = (secretaire) => {
    setCurrentSecretaire(secretaire);
    setIsEditMode(true);
    setIsOpen(true);
    setNameWarning("");
    setFieldErrors({});
    setShowPassword(false);
    setShowCPassword(false);
    setShowPasswordEdit(false);
    setShowCPasswordEdit(false);
    setChangePassword(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setCurrentSecretaire(null);
    setIsEditMode(false);
    setNameWarning("");
    setFieldErrors({});
    setShowPassword(false);
    setShowCPassword(false);
    setShowPasswordEdit(false);
    setShowCPasswordEdit(false);
    setChangePassword(false);
  };

  const handleSaveSecretaire = async (formData) => {
    setIsSubmitting(true);
    setFieldErrors({});
    try {
      let secretaireData = {
        first_name: formData.get("first_name"),
        last_name: formData.get("last_name"),
        email_user: formData.get("email_user"),
      };
      if (isEditMode && changePassword) {
        secretaireData.password = formData.get("password");
        secretaireData.c_password = formData.get("c_password");
      }
      if (!isEditMode) {
        secretaireData.password = formData.get("password");
        secretaireData.c_password = formData.get("c_password");
      }
      const id = formData.get("id");
      let response;
      if (isEditMode) {
        response = await secretairesService.update(id, secretaireData);
      } else {
        response = await secretairesService.create(secretaireData);
      }

      if (response && response.success) {
        fetchSecretaires();
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: isEditMode
            ? "Secrétaire mis à jour avec succès!"
            : "Secrétaire ajouté avec succès!",
          confirmButtonColor: "#57A863",
        });
        handleClose();
      } else {
        // Gestion field error (validation Laravel)
        if (
          response?.message &&
          typeof response.message === "object"
        ) {
          setFieldErrors(response.message);
          return;
        }
        let errorMessage =
          response?.message ||
          response?.data ||
          "Erreur inconnue lors de l'enregistrement";
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: errorMessage,
          confirmButtonColor: "#d33",
        });
      }
    } catch (error) {
      if (
        error.response?.data?.message &&
        typeof error.response.data.message === "object"
      ) {
        setFieldErrors(error.response.data.message);
        return;
      }
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text:
          error.message ||
          "Une erreur s'est produite lors de l'enregistrement de la secrétaire.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    const formData = new FormData(e.target);

    const firstName = formData.get("first_name");
    const lastName = formData.get("last_name");
    const email = formData.get("email_user");
    const currentId = isEditMode ? parseInt(formData.get("id")) : null;

    let warnings = [];
    if (checkDuplicateName(firstName, lastName, currentId)) {
      warnings.push(`Le nom "${firstName} ${lastName}" existe déjà`);
    }
    if (checkDuplicateEmail(email, currentId)) {
      warnings.push(`L'email "${email}" est déjà utilisé`);
    }

    if (warnings.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Attention - Doublons détectés",
        html: `
          <div class="text-left">
            <ul class="list-disc list-inside text-sm text-red-600 space-y-1 mb-4">
              ${warnings.map((warning) => `<li>${warning}</li>`).join("")}
            </ul>
            <p class="text-sm text-gray-600">Voulez-vous continuer quand même ?</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Continuer",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#d97706",
        cancelButtonColor: "#57A863",
      }).then((result) => {
        if (result.isConfirmed) {
          handleSaveSecretaire(formData);
        }
      });
      return;
    }

    await handleSaveSecretaire(formData);
  };

  const handleDeleteSecretaire = async (secretaire) => {
    const result = await Swal.fire({
      title: "Êtes-vous sûr ?",
      html: `Voulez-vous vraiment supprimer <strong>${secretaire.first_name} ${secretaire.last_name}</strong> ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
    });

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: "Suppression en cours...",
          html: `
            <div class="flex flex-col items-center">
              <div class="relative w-16 h-16">
                <div class="absolute top-0 left-0 w-16 h-16 border-4 border-red-500 border-opacity-20 rounded-full"></div>
                <div class="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
              </div>
              <p class="mt-4 text-gray-700">Suppression en cours...</p>
            </div>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
        });

        const response = await secretairesService.delete(secretaire.id);
        Swal.close();

        if (response && response.success) {
          setSecretaires((prev) => prev.filter((s) => s.id !== secretaire.id));
          Swal.fire({
            icon: "success",
            title: "Supprimé !",
            text: `${secretaire.first_name} ${secretaire.last_name} a été supprimé avec succès.`,
            confirmButtonColor: "#57A863",
          });
        } else {
          throw new Error(response?.message || "Échec de la suppression");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text:
            error.message ||
            "Une erreur s'est produite lors de la suppression du secrétaire.",
          confirmButtonColor: "#d33",
        });
      }
    }
  };

  // Affichage détails
  const handleViewDetails = (secretaire) => {
    Swal.fire({
      title: `${secretaire.first_name} ${secretaire.last_name}`,
      html: `
        <div class="text-left space-y-3">
          <div>
            <strong>Email:</strong> ${secretaire.email_user}
          </div>
          <div>
            <strong>Créé le:</strong> ${new Date(
              secretaire.created_at
            ).toLocaleDateString("fr-FR", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      `,
      showCloseButton: true,
      confirmButtonText: "Fermer",
      confirmButtonColor: "#57A863",
      width: "600px",
    });
  };

  return (
    <div className="mt-5 flex h-full">
      <CardSecretaires
        isOpen={handleNew}
        users={secretaires}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDeleteSecretaire}
        onViewDetails={handleViewDetails}
      />

      <ModalAddSecretaire
        isOpen={isOpen}
        onClose={handleClose}
        width="64"
        title={
          isEditMode ? "Modifier une secrétaire" : "Ajouter une secrétaire"
        }
      >
        <form onSubmit={handleSubmit} autoComplete="off">
          <input type="hidden" name="id" value={currentSecretaire?.id || ""} />

          <div className="mb-4">
            <label
              htmlFor="first_name"
              className="mb-2 block text-sm font-medium"
            >
              Nom *
            </label>
            <input
              type="text"
              name="first_name"
              id="first_name"
              placeholder="Nom de la secrétaire"
              className={`focus:border-transparent w-full rounded-lg border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863] ${
                fieldErrors.first_name
                  ? "border-red-500 ring-red-200"
                  : "border-gray-300"
              }`}
              defaultValue={currentSecretaire?.first_name || ""}
              onChange={(e) => {
                const lastName =
                  document.getElementById("last_name")?.value || "";
                handleNameChange(e.target.value, lastName);
              }}
              required
            />
            {fieldErrors.first_name && (
              <div className="mt-1 flex items-center text-sm text-red-600">
                <svg
                  className="mr-1 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    d="M9 9l6 6m0-6l-6 6"
                  />
                </svg>
                {fieldErrors.first_name[0]}
              </div>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="last_name"
              className="mb-2 block text-sm font-medium"
            >
              Prénoms *
            </label>
            <input
              type="text"
              name="last_name"
              id="last_name"
              placeholder="Prénoms de la secrétaire"
              className={`focus:border-transparent w-full rounded-lg border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863] ${
                fieldErrors.last_name
                  ? "border-red-500 ring-red-200"
                  : "border-gray-300"
              }`}
              defaultValue={currentSecretaire?.last_name || ""}
              onChange={(e) => {
                const firstName =
                  document.getElementById("first_name")?.value || "";
                handleNameChange(firstName, e.target.value);
              }}
              required
            />
            {nameWarning && (
              <div className="mt-2 flex items-center text-sm text-amber-600">
                <svg
                  className="mr-1 h-4 w-4 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{nameWarning}</span>
              </div>
            )}
            {fieldErrors.last_name && (
              <div className="mt-1 flex items-center text-sm text-red-600">
                <svg
                  className="mr-1 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    d="M9 9l6 6m0-6l-6 6"
                  />
                </svg>
                {fieldErrors.last_name[0]}
              </div>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="email_user"
              className="mb-2 block text-sm font-medium"
            >
              Email *
            </label>
            <input
              type="email"
              name="email_user"
              id="email_user"
              placeholder="email@example.com"
              className={`focus:border-transparent w-full rounded-lg border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863] ${
                fieldErrors.email_user
                  ? "border-red-500 ring-red-200"
                  : "border-gray-300"
              }`}
              defaultValue={currentSecretaire?.email_user || ""}
              required
            />
            {fieldErrors.email_user && (
              <div className="mt-1 flex items-center text-sm text-red-600">
                <svg
                  className="mr-1 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    d="M9 9l6 6m0-6l-6 6"
                  />
                </svg>
                {fieldErrors.email_user[0]}
              </div>
            )}
          </div>

          {/* Mot de passe avec "yeux" */}
          {!isEditMode && (
            <>
              <div className="relative mb-4">
                <label
                  htmlFor="password"
                  className="mb-2 block text-sm font-medium"
                >
                  Mot de passe *
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  placeholder="Mot de passe"
                  className={`focus:border-transparent w-full rounded-lg border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863] ${
                    fieldErrors.password
                      ? "border-red-500 ring-red-200"
                      : "border-gray-300"
                  }`}
                  required
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-700"
                  onClick={() => setShowPassword((v) => !v)}
                  title={showPassword ? "Masquer" : "Afficher"}
                >
                  {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                </span>
                {fieldErrors.password && (
                  <div className="mt-1 flex items-center text-sm text-red-600">
                    <svg
                      className="mr-1 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        stroke="currentColor"
                        strokeWidth="2"
                        d="M9 9l6 6m0-6l-6 6"
                      />
                    </svg>
                    {fieldErrors.password[0]}
                  </div>
                )}
              </div>
              <div className="relative mb-4">
                <label
                  htmlFor="c_password"
                  className="mb-2 block text-sm font-medium"
                >
                  Confirmation du mot de passe *
                </label>
                <input
                  type={showCPassword ? "text" : "password"}
                  name="c_password"
                  id="c_password"
                  placeholder="Confirmation"
                  className={`focus:border-transparent w-full rounded-lg border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863] ${
                    fieldErrors.c_password
                      ? "border-red-500 ring-red-200"
                      : "border-gray-300"
                  }`}
                  required
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-700"
                  onClick={() => setShowCPassword((v) => !v)}
                  title={showCPassword ? "Masquer" : "Afficher"}
                >
                  {showCPassword ? <MdVisibilityOff /> : <MdVisibility />}
                </span>
                {fieldErrors.c_password && (
                  <div className="mt-1 flex items-center text-sm text-red-600">
                    <svg
                      className="mr-1 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        stroke="currentColor"
                        strokeWidth="2"
                        d="M9 9l6 6m0-6l-6 6"
                      />
                    </svg>
                    {fieldErrors.c_password[0]}
                  </div>
                )}
              </div>
            </>
          )}

          {isEditMode && (
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={changePassword}
                  onChange={() => {
                    setChangePassword((v) => !v);
                    setFieldErrors((prev) => ({
                      ...prev,
                      password: undefined,
                      c_password: undefined,
                    }));
                  }}
                />
                Changer le mot de passe
              </label>
              {changePassword && (
                <>
                  <div className="relative mb-2">
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-medium"
                    >
                      Nouveau mot de passe
                    </label>
                    <input
                      type={showPasswordEdit ? "text" : "password"}
                      name="password"
                      id="password"
                      placeholder="Nouveau mot de passe"
                      className={`focus:border-transparent w-full rounded-lg border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863] ${
                        fieldErrors.password
                          ? "border-red-500 ring-red-200"
                          : "border-gray-300"
                      }`}
                      required={changePassword}
                    />
                    <span
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-700"
                      onClick={() => setShowPasswordEdit((v) => !v)}
                      title={showPasswordEdit ? "Masquer" : "Afficher"}
                    >
                      {showPasswordEdit ? <MdVisibilityOff /> : <MdVisibility />}
                    </span>
                    {fieldErrors.password && (
                      <div className="mt-1 flex items-center text-sm text-red-600">
                        <svg
                          className="mr-1 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                          <path
                            stroke="currentColor"
                            strokeWidth="2"
                            d="M9 9l6 6m0-6l-6 6"
                          />
                        </svg>
                        {fieldErrors.password[0]}
                      </div>
                    )}
                  </div>
                  <div className="relative mb-2">
                    <label
                      htmlFor="c_password"
                      className="mb-2 block text-sm font-medium"
                    >
                      Confirmation du nouveau mot de passe
                    </label>
                    <input
                      type={showCPasswordEdit ? "text" : "password"}
                      name="c_password"
                      id="c_password"
                      placeholder="Confirmation"
                      className={`focus:border-transparent w-full rounded-lg border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863] ${
                        fieldErrors.c_password
                          ? "border-red-500 ring-red-200"
                          : "border-gray-300"
                      }`}
                      required={changePassword}
                    />
                    <span
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-700"
                      onClick={() => setShowCPasswordEdit((v) => !v)}
                      title={showCPasswordEdit ? "Masquer" : "Afficher"}
                    >
                      {showCPasswordEdit ? <MdVisibilityOff /> : <MdVisibility />}
                    </span>
                    {fieldErrors.c_password && (
                      <div className="mt-1 flex items-center text-sm text-red-600">
                        <svg
                          className="mr-1 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                          <path
                            stroke="currentColor"
                            strokeWidth="2"
                            d="M9 9l6 6m0-6l-6 6"
                          />
                        </svg>
                        {fieldErrors.c_password[0]}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="mb-6 rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 text-sm font-medium text-gray-700">
              Informations
            </h4>
            <div className="space-y-1 text-xs text-gray-600">
              <div>• Tous les champs marqués d'un * sont obligatoires</div>
              <div>• L'email doit être unique dans le système</div>
              <div>• Le nom et prénom doivent être uniques</div>
              {!isEditMode && (
                <div>• Le mot de passe doit être renseigné lors de l'ajout</div>
              )}
              {isEditMode && (
                <div>• Cochez "Changer le mot de passe" pour le modifier</div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg bg-gray-200 px-5 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Annuler
            </button>

            <button
              type="submit"
              className="rounded-lg bg-gradient-to-r from-[#57A863] to-[#3d9448] px-5 py-2.5 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="mr-2 h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Traitement...
                </span>
              ) : (
                <span className="flex items-center">
                  <MdCheckCircle className="mr-1.5 h-4 w-4" />
                  {isEditMode ? "Mettre à jour" : "Ajouter"}
                </span>
              )}
            </button>
          </div>
        </form>
      </ModalAddSecretaire>
    </div>
  );
};

export default Secretaires;